import React, { useState, useEffect, useRef } from 'react';
import FeatherIcon from 'feather-icons-react';
import { useBrandVoice } from '../contexts/BrandVoiceContext';
import { GEO_OPTIONS } from '../constants/trendGeoOptions';
import BrandVoiceFields from './BrandVoiceFields';
import './SettingsPanel.css';

const BLOG_LANG_OPTIONS = [
  { code: 'nl', label: 'Dutch', flagCode: 'nl' },
  { code: 'en', label: 'English', flagCode: 'gb' }
];

export default function SettingsPanel() {
  const {
    geo,
    setGeo,
    blogOutputLanguage,
    setBlogOutputLanguage,
    isSettingsPanelActive
  } = useBrandVoice();

  const [isExpanded, setIsExpanded] = useState(false);
  const [showGeoDropdown, setShowGeoDropdown] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const geoDropdownRef = useRef(null);
  const langDropdownRef = useRef(null);

  const isGeoActive = geo !== 'NL';

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (geoDropdownRef.current && !geoDropdownRef.current.contains(e.target)) {
        setShowGeoDropdown(false);
      }
      if (langDropdownRef.current && !langDropdownRef.current.contains(e.target)) {
        setShowLangDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleGeoReset = () => {
    setGeo('NL');
  };

  const selectedGeo = GEO_OPTIONS.find(o => o.code === geo) || GEO_OPTIONS[0];
  const selectedLang = BLOG_LANG_OPTIONS.find(o => o.code === blogOutputLanguage) || BLOG_LANG_OPTIONS[0];

  return (
    <div className="settings-panel trend-settings">
      <div
        className="brand-voice-header"
        onClick={() => setIsExpanded((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsExpanded((prev) => !prev);
          }
        }}
        role="button"
        tabIndex={0}
      >
        <span className="brand-voice-label">Settings</span>
        <FeatherIcon
          icon={isExpanded ? 'chevron-up' : 'chevron-down'}
          size={20}
          className="chevron-icon"
        />
        {isSettingsPanelActive && <span className="brand-voice-indicator" />}
      </div>

      {isExpanded && (
        <div className="settings-panel-body">
          <section className="settings-panel-section" aria-labelledby="settings-search-heading">
            <h3 id="settings-search-heading" className="settings-panel-section-title">
              Search
            </h3>
            <div className="brand-voice-form">
              <div className="form-field form-field--geo-row">
                <label>Region</label>
                <div className="geo-selector-inner" ref={geoDropdownRef}>
                  <button
                    type="button"
                    className="geo-select-trigger geo-select-trigger--sm"
                    onClick={() => {
                      setShowLangDropdown(false);
                      setShowGeoDropdown((prev) => !prev);
                    }}
                    aria-haspopup="listbox"
                    aria-expanded={showGeoDropdown}
                  >
                    <img
                      src={`https://flagcdn.com/20x15/${geo.toLowerCase()}.png`}
                      srcSet={`https://flagcdn.com/40x30/${geo.toLowerCase()}.png 2x`}
                      alt={selectedGeo?.name}
                      className="geo-flag-img"
                    />
                    <span className="geo-select-label">{selectedGeo?.name}</span>
                    <span className={`geo-select-chevron${showGeoDropdown ? ' open' : ''}`}>▾</span>
                  </button>
                  {showGeoDropdown && (
                    <ul className="geo-dropdown-list" role="listbox">
                      {GEO_OPTIONS.map(({ code, name }) => (
                        <li
                          key={code}
                          role="option"
                          aria-selected={geo === code}
                          className={`geo-dropdown-item${geo === code ? ' selected' : ''}`}
                          onClick={() => {
                            setGeo(code);
                            setShowGeoDropdown(false);
                          }}
                        >
                          <img
                            src={`https://flagcdn.com/20x15/${code.toLowerCase()}.png`}
                            srcSet={`https://flagcdn.com/40x30/${code.toLowerCase()}.png 2x`}
                            alt={name}
                            className="geo-flag-img"
                          />
                          <span>{name}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {isGeoActive && (
                <button type="button" className="clear-button ts-clear-button" onClick={handleGeoReset}>
                  <FeatherIcon icon="x" size={16} />
                  Reset
                </button>
              )}
            </div>
          </section>

          <section className="settings-panel-section" aria-labelledby="settings-brand-heading">
            <h3 id="settings-brand-heading" className="settings-panel-section-title">
              Brand voice
            </h3>
            <BrandVoiceFields />
          </section>

          <section className="settings-panel-section" aria-labelledby="settings-blog-heading">
            <h3 id="settings-blog-heading" className="settings-panel-section-title">
              Blog output
            </h3>
            <div className="brand-voice-form">
              <div className="form-field form-field--geo-row">
                <label htmlFor="blog-lang-trigger" id="blog-lang-label">Language</label>
                <div className="geo-selector-inner" ref={langDropdownRef}>
                  <button
                    id="blog-lang-trigger"
                    type="button"
                    className="geo-select-trigger geo-select-trigger--sm"
                    onClick={() => {
                      setShowGeoDropdown(false);
                      setShowLangDropdown((prev) => !prev);
                    }}
                    aria-haspopup="listbox"
                    aria-expanded={showLangDropdown}
                    aria-labelledby="blog-lang-label"
                  >
                    <img
                      src={`https://flagcdn.com/20x15/${selectedLang.flagCode}.png`}
                      srcSet={`https://flagcdn.com/40x30/${selectedLang.flagCode}.png 2x`}
                      alt=""
                      className="geo-flag-img"
                    />
                    <span className="geo-select-label">{selectedLang.label}</span>
                    <span className={`geo-select-chevron${showLangDropdown ? ' open' : ''}`}>▾</span>
                  </button>
                  {showLangDropdown && (
                    <ul className="geo-dropdown-list" role="listbox" aria-labelledby="blog-lang-label">
                      {BLOG_LANG_OPTIONS.map(({ code, label, flagCode }) => (
                        <li
                          key={code}
                          role="option"
                          aria-selected={blogOutputLanguage === code}
                          className={`geo-dropdown-item${blogOutputLanguage === code ? ' selected' : ''}`}
                          onClick={() => {
                            setBlogOutputLanguage(code);
                            setShowLangDropdown(false);
                          }}
                        >
                          <img
                            src={`https://flagcdn.com/20x15/${flagCode}.png`}
                            srcSet={`https://flagcdn.com/40x30/${flagCode}.png 2x`}
                            alt=""
                            className="geo-flag-img"
                          />
                          <span>{label}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
